import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as api from "../api";
import { LegEditor, LegRow, blankLeg } from "../components/LegEditor";
import { GatesPanel } from "../components/GatesPanel";
import { PayoffChart } from "../components/PayoffChart";
import { PriceHistoryChart } from "../components/PriceHistoryChart";
import { LBL, tip } from "../labels";
import { Tip } from "../components/Tooltip";
import { breakevenFromPayoff } from "../lib/breakeven";

const RISK_FREE_RATE = 0.05; // захардкодим — это параметр BS, не доходность депозита

export function PortfolioBuilderPage() {
  const { id } = useParams<{ id?: string }>();
  const isNew = !id;
  const nav = useNavigate();

  const [name, setName] = useState("Новый портфель");
  const [legs, setLegs] = useState<LegRow[]>([]);
  const [loaded, setLoaded] = useState(isNew);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Default underlying + market params
  const [underlying, setUnderlying] = useState("GLD");
  // Комбобокс тикера: клик по полю → список активов из вотчлиста (серверный реестр)
  const [wl, setWl] = useState<api.TrackedSymbolOut[]>([]);
  const [tickerOpen, setTickerOpen] = useState(false);
  useEffect(() => { api.fetchTrackedSymbols(true).then(setWl).catch(() => {}); }, []);
  const [spot, setSpot] = useState(100);
  const [iv, setIv] = useState(0.20);
  const [ivRank, setIvRank] = useState<number | "">("");
  const riskFree = RISK_FREE_RATE;

  // Дата выхода / горизонт (P0-1/P0-2). Дефолт = сегодня + 9 месяцев — методика
  // «Айкидо» выходит на ~9 мес, НЕ держит до полного распада theta в экспирацию.
  const [exitDate, setExitDate] = useState<string>(defaultExitDate());

  // Пикер реальной экспирации (spec Market Data, Правило 1)
  const [expirations, setExpirations] = useState<api.ExpirationsResponse | null>(null);
  const [loadingExp, setLoadingExp] = useState(false);
  // Выбранный срок — хранится отдельно от ног, чтобы выбор «жил» даже до сборки стрэнгла.
  const [chosenExpiry, setChosenExpiry] = useState<string>("");

  async function loadExpirations() {
    setLoadingExp(true); setError(null);
    try {
      const r = await api.fetchExpirations(underlying);
      setExpirations(r);
      if (r.best_expiration) { setChosenExpiry(r.best_expiration); applyExpiration(r.best_expiration); }
    } catch (e: any) {
      setError(`Экспирации: ${e.message ?? e}`);
    } finally { setLoadingExp(false); }
  }

  // Сменить выбранный срок: запоминаем и применяем к опционным ногам (если есть).
  function chooseExpiry(iso: string) { setChosenExpiry(iso); applyExpiration(iso); }

  // Применить выбранную экспирацию ко всем опционным ногам.
  function applyExpiration(iso: string) {
    setLegs((prev) => prev.map((l) => (l.type === "option" ? { ...l, expiry: iso } : l)));
  }

  // Capital для backend evaluate — computed из legs (всех опционов + депозитов + кэша + облигаций)
  const capital = useMemo(() => {
    let opts = 0, depos = 0, cash = 0, bonds = 0;
    for (const l of legs) {
      if (l.type === "option") opts += (l.premium_paid ?? 0) * (l.contract_multiplier ?? 1) * (l.quantity ?? 0);
      else if (l.type === "deposit") depos += (l.principal ?? 0) * (l.quantity ?? 0);
      else if (l.type === "cash") cash += (l.quantity ?? 0);
      else if (l.type === "bond") bonds += (l.purchase_price ?? 0) * (l.quantity ?? 0);
    }
    return opts + depos + cash + bonds;
  }, [legs]);
  // depositRate для Авто-strangle создания ноги — берём из существующей либо дефолт 5%
  const depositRate = legs.find((l) => l.type === "deposit")?.annual_rate ?? 0.05;

  // Slider state for payoff date
  const [dateIndex, setDateIndex] = useState(0);

  // Last evaluation + payoff response
  const [evaluation, setEvaluation] = useState<api.EvaluateResponse | null>(null);
  const [payoff, setPayoff] = useState<api.PayoffResponse | null>(null);
  const [computing, setComputing] = useState(false);

  // Autofill state
  const [autofilling, setAutofilling] = useState(false);
  const [marketInfo, setMarketInfo] = useState<string | null>(null);
  const [pullStep, setPullStep] = useState<string | null>(null);
  const [pctOtm, setPctOtm] = useState(3);

  async function pullMarket() {
    setAutofilling(true); setError(null); setMarketInfo(null);
    setPullStep(`Котировка ${underlying} из терминала…`);
    try {
      const q = await api.fetchQuote(underlying);
      setSpot(Number(q.last_price.toFixed(4)));
      let info = `${underlying}: ${q.last_price} ${q.currency} ${q.quality_marker} (${q.source}, +${q.delay_minutes}мин)${qualitySuffix(q.stale, q.cache_age_s)}`;
      setPullStep(`IV Rank ${underlying}…`);
      try {
        const ivr = await api.fetchIvRank(underlying);
        if (ivr.iv_rank_pct !== null && ivr.iv_rank_pct !== undefined) {
          setIvRank(Number(ivr.iv_rank_pct.toFixed(1)));
          if (ivr.current_iv && ivr.current_iv > 0.03) setIv(Number(ivr.current_iv.toFixed(4)));
          info += ` · IV Rank ${ivr.iv_rank_pct.toFixed(0)}%`;
          if (ivr.note) info += ` (${ivr.note.slice(0, 60)})`;
        } else {
          info += " · IV Rank н/д (введите вручную из IBKR: «52-нед. рейтинг ПВ»)";
        }
      } catch { /* iv-rank опционален */ }
      setMarketInfo(info);
    } catch (e: any) {
      setError(`Автофилл цены: ${e.message}`);
    } finally { setAutofilling(false); setPullStep(null); }
  }

  // Сборка стрэнгла. Возвращает собранное (для drill-down из вотчлиста, где
  // нельзя полагаться на ещё не применённый setState).
  async function buildStrangle(sym: string): Promise<{ legs: LegRow[]; spot: number; iv: number; ivRank: number | "" } | null> {
    setAutofilling(true); setError(null); setMarketInfo(null);
    // Целевой срок = ВЫБРАННАЯ экспирация (из пикера/ног), иначе ~оптимальные 365д.
    // Бэкенд берёт ближайшую доступную экспирацию к этому dte → для реальной выбранной даты это она сама.
    const selExp = chosenExpiry || legs.find((l) => l.type === "option")?.expiry || expirations?.best_expiration || null;
    const targetDte = selExp
      ? Math.max(1, Math.min(1000, Math.round((new Date(selExp + "T00:00:00").getTime() - Date.now()) / 86_400_000)))
      : 365;
    setPullStep(selExp ? `Цепочка ${sym} на ${selExp} (~${targetDte}д)…` : `Опционная цепочка ${sym} (~оптимальный срок)…`);
    try {
      const chain = await api.fetchChain(sym, targetDte, pctOtm);
      if (chain.stale) setError("⚠ Источник котировок временно недоступен — показаны последние сохранённые данные (могут устареть).");
      setSpot(Number(chain.underlying_price.toFixed(4)));
      const { call, put } = chain.suggested;
      if (!call || !put) {
        setError("В цепочке не нашлось подходящих страйков");
        return null;
      }

      // 1) Опционы — premium = MID (середина bid/ask), реалистичная цена лимитного ордера,
      //    не ask (верхняя граница спреда). Если есть и bid, и ask > 0 → mid.
      //    Фолбэк при пустых котировках: last → ask → bid (первое положительное).
      //    ВАЖНО: 0/null отсеиваем (иначе у неликвида FXE стрэнгл собирался «бесплатным»).
      const pos = (v: number | null | undefined): v is number => v != null && v > 0;
      const firstPos = (...vals: (number | null | undefined)[]) => vals.find(pos);
      const priceOf = (o: { bid?: number | null; ask?: number | null; premium?: number | null }): { px?: number; basis: string } => {
        if (pos(o.bid) && pos(o.ask)) return { px: +(((o.bid! + o.ask!) / 2)).toFixed(4), basis: "mid" };
        const px = firstPos(o.premium, o.ask, o.bid);
        return { px, basis: pos(o.premium) ? "last" : pos(o.ask) ? "ask" : "bid" };
      };
      const cP = priceOf(call), pP = priceOf(put);
      const callPremium = cP.px, putPremium = pP.px;
      if (!callPremium || !putPremium) {
        setError(
          `${sym}: в цепочке нет цен (bid/ask/last пусты или 0) — источник не котирует эти страйки (неликвид). ` +
          `Стрэнгл не собран, иначе опционы вышли бы «бесплатными». Возьми котировку из терминала и заполни ноги вручную.`
        );
        return null;
      }
      const callBasis = cP.basis, putBasis = pP.basis;

      const callLeg: any = {
        type: "option", name: `${sym} Call ${call.strike}`, quantity: 1,
        side: "call", underlying: sym, strike: call.strike, expiry: chain.expiry,
        premium_paid: callPremium, contract_multiplier: 100,
        bid: call.bid, ask: call.ask, last: call.premium,
        iv_market: call.iv, open_interest: call.open_interest,
        _internal_id: Math.random().toString(36).slice(2),
      };
      const putLeg: any = {
        type: "option", name: `${sym} Put ${put.strike}`, quantity: 1,
        side: "put", underlying: sym, strike: put.strike, expiry: chain.expiry,
        premium_paid: putPremium, contract_multiplier: 100,
        bid: put.bid, ask: put.ask, last: put.premium,
        iv_market: put.iv, open_interest: put.open_interest,
        _internal_id: Math.random().toString(36).slice(2),
      };

      // Стрэдл — это 2 ноги опционов. Депозит добавляется отдельно через
      // «+ Добавить ногу» → «Депозит», когда пользователь хочет проверить gates.
      // Сохраняем существующие не-опционные ноги (если они уже были в портфеле).
      const nonOptions = legs.filter((l) => l.type !== "option");
      const builtLegs: LegRow[] = [callLeg, putLeg, ...nonOptions];
      setLegs(builtLegs);

      // 3) ATM IV из цепочки — для корректной BS-проекции (theta decay)
      const validAtmIv = chain.atm_iv;
      if (validAtmIv && validAtmIv > 0.03) {
        setIv(Number(validAtmIv.toFixed(4)));
      }
      // 4) IV Rank — автоподстановка (если backend смог посчитать)
      if (chain.iv_rank_pct !== null && chain.iv_rank_pct !== undefined) {
        setIvRank(Number(chain.iv_rank_pct.toFixed(1)));
      }

      const ivWarning = !validAtmIv
        ? " ⚠ IV из цепочки не валидна"
        : "";
      const callTotal = (callPremium * 100).toFixed(0);
      const putTotal = (putPremium * 100).toFixed(0);
      const ivRankInfo = chain.iv_rank_pct !== null && chain.iv_rank_pct !== undefined
        ? ` · IV Rank ${chain.iv_rank_pct.toFixed(0)}%`
        : "";
      setMarketInfo(
        `${sym} ${chain.quality_marker} · spot ${chain.underlying_price.toFixed(2)} · ` +
        `Call ${call.strike}@${callPremium} (${callBasis}, итого $${callTotal}/контр.) / ` +
        `Put ${put.strike}@${putPremium} (${putBasis}, итого $${putTotal}/контр.) · ` +
        `DTE ${chain.dte} · OI ${call.open_interest}/${put.open_interest} · ATM IV ${validAtmIv ? (validAtmIv * 100).toFixed(1) + "%" : "н/д"}${ivRankInfo}${ivWarning}` +
        (callBasis !== "mid" || putBasis !== "mid" ? ` · ⚠ нет полного bid/ask — премия по «${callBasis}/${putBasis}», реальная цена может отличаться (проверь в терминале)` : " · премия = mid (середина bid/ask)")
      );
      return {
        legs: builtLegs,
        spot: +chain.underlying_price.toFixed(4),
        iv: validAtmIv && validAtmIv > 0.03 ? +validAtmIv.toFixed(4) : iv,
        ivRank: chain.iv_rank_pct !== null && chain.iv_rank_pct !== undefined ? +chain.iv_rank_pct.toFixed(1) : ("" as const),
      };
    } catch (e: any) {
      setError(`Авто-strangle: ${e.message}`);
      return null;
    } finally { setAutofilling(false); setPullStep(null); }
  }

  async function autoStrangle() { await buildStrangle(underlying); }

  function addStrangleTemplate() {
    const expiryDate = new Date(); expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    const expiry = expiryDate.toISOString().slice(0, 10);
    const callStrike = +(spot * (1 + pctOtm / 100)).toFixed(2);
    const putStrike = +(spot * (1 - pctOtm / 100)).toFixed(2);
    const premiumPerUnit = +(spot * 0.025).toFixed(2);

    // Long strangle = 2 опционные ноги. Депозит добавляется отдельно через «+ Добавить ногу»
    const template: any[] = [
      {
        type: "option", name: `${underlying} Call ${callStrike}`, quantity: 1,
        side: "call", underlying, strike: callStrike, expiry,
        premium_paid: premiumPerUnit, contract_multiplier: 100,
        _internal_id: Math.random().toString(36).slice(2),
      },
      {
        type: "option", name: `${underlying} Put ${putStrike}`, quantity: 1,
        side: "put", underlying, strike: putStrike, expiry,
        premium_paid: premiumPerUnit, contract_multiplier: 100,
        _internal_id: Math.random().toString(36).slice(2),
      },
    ];
    setLegs(template);
    setMarketInfo("Шаблон создан. Подставь реальные strike/premium из терминала. Для проверки gates 90/10 и no-loss — добавь ногу «Депозит».");
  }

  // --- Drill-down из вотчлиста: /portfolios/new?symbol=GLD[&from=XAU/USD] ---
  // Автоматически: тикер → сборка стрэнгла из цепочки → расчёт профиля + gates.
  // Портфель один на тикер (reuseByName) — повторные заходы не плодят драфты.
  const [searchParams] = useSearchParams();
  const drillRan = useRef(false);
  useEffect(() => {
    const qsym = searchParams.get("symbol");
    if (!isNew || !qsym || drillRan.current) return;
    drillRan.current = true;
    const sym = qsym.toUpperCase();
    const fromTicker = searchParams.get("from") || sym;
    const autoName = `${fromTicker} · стрэнгл из вотчлиста`;
    setUnderlying(sym);
    setName(autoName);
    (async () => {
      const built = await buildStrangle(sym);
      if (!built) return;   // ошибка уже показана buildStrangle'ом
      await compute({ ...built, underlying: sym, name: autoName, reuseByName: true });
    })();
    // eslint-disable-next-line
  }, [isNew, searchParams]);

  useEffect(() => {
    if (isNew) return;
    api.getPortfolio(id!)
      .then((p) => {
        setName(p.name);
        setLegs(p.instruments.map((i: any) => ({ ...i, _internal_id: i.id })));
        const underlyings = p.instruments.map((i: any) => i.underlying).filter(Boolean);
        if (underlyings.length) setUnderlying(underlyings[0]);
        setLoaded(true);
      })
      .catch((e) => setError(e.message));
  }, [id, isNew]);

  function buildPayloadInstruments() {
    return legs.map((l) => {
      const { _internal_id, ...rest } = l;
      return rest;
    });
  }

  async function save() {
    setSaving(true); setError(null);
    try {
      const payload = { name, instruments: buildPayloadInstruments() };
      if (isNew) {
        const p = await api.createPortfolio(payload);
        nav(`/portfolios/${p.id}`, { replace: true });
      } else {
        await api.updatePortfolio(id!, payload);
      }
    } catch (e: any) {
      setError(e.message);
    } finally { setSaving(false); }
  }

  // ov — overrides для drill-down из вотчлиста (state ещё не применён к моменту вызова).
  // reuseByName: не плодить драфты — повторный заход по тому же тикеру обновляет один портфель.
  async function compute(ov?: { legs?: LegRow[]; underlying?: string; spot?: number; iv?: number; ivRank?: number | ""; name?: string; reuseByName?: boolean }) {
    const effLegs = ov?.legs ?? legs;
    const effUnd = ov?.underlying ?? underlying;
    const effSpot = ov?.spot ?? spot;
    const effIv = ov?.iv ?? iv;
    const effIvRank = ov?.ivRank !== undefined ? ov.ivRank : ivRank;
    const effName = ov?.name ?? name;
    const instruments = effLegs.map((l) => { const { _internal_id, ...rest } = l as any; return rest; });
    setComputing(true); setError(null);
    try {
      // 1. Save first to get an id
      let portfolioId = id;
      if (isNew || !portfolioId) {
        let existing: any = null;
        if (ov?.reuseByName) {
          try { existing = (await api.listPortfolios()).find((p: any) => p.name === effName); } catch { /* */ }
        }
        if (existing) {
          portfolioId = existing.id;
          await api.updatePortfolio(portfolioId!, { name: effName, instruments });
          nav(`/portfolios/${portfolioId}`, { replace: true });
        } else {
          const p = await api.createPortfolio({ name: effName, instruments });
          portfolioId = p.id;
          nav(`/portfolios/${p.id}`, { replace: true });
        }
      } else {
        await api.updatePortfolio(portfolioId, { name: effName, instruments });
      }
      if (!portfolioId) { setError("Не удалось получить ID портфеля"); return; }
      // 2. Даты: сетка идёт до РЕАЛЬНОЙ экспирации — тогда последняя точка графика
      //    это intrinsic-payoff (kinked «галка»), а не сглаженная BS-кривая.
      //    Дата выхода (exit) — отдельное понятие: на ней считается вердикт gates,
      //    и слайдер по умолчанию встаёт на неё.
      const todayISO = new Date().toISOString().slice(0, 10);
      const expiries = effLegs
        .filter((l) => l.type === "option")
        .map((l: any) => l.expiry)
        .filter(Boolean) as string[];
      const sortedExp = expiries.slice().sort();
      const nearestExpiry = sortedExp[0];
      const lastExpiry = sortedExp[sortedExp.length - 1] || todayISO;
      // exit_date не позже ближайшей экспирации и не раньше сегодня
      let effExit = exitDate || todayISO;
      if (nearestExpiry && effExit > nearestExpiry) effExit = nearestExpiry;
      if (effExit < todayISO) effExit = todayISO;

      // Сетка today → РЕАЛЬНАЯ экспирация (последняя точка = T=0 → kinked).
      const dates = makeDateGrid(todayISO, lastExpiry, 12);

      const effCapital = ov?.legs
        ? effLegs.reduce((s, l: any) => s + (l.type === "option" ? (l.premium_paid ?? 0) * (l.contract_multiplier ?? 1) * (l.quantity ?? 0)
            : l.type === "deposit" ? (l.principal ?? 0) * (l.quantity ?? 0)
            : l.type === "cash" ? (l.quantity ?? 0)
            : l.type === "bond" ? (l.purchase_price ?? 0) * (l.quantity ?? 0) : 0), 0)
        : capital;
      const market = { spot: { [effUnd]: effSpot }, iv: { [effUnd]: effIv }, risk_free_rate: riskFree };
      const ev = await api.evaluatePortfolio(portfolioId, {
        valuation_date: todayISO,
        exit_date: effExit,          // вердикт gates — на дату выхода
        market,
        portfolio_capital: effCapital,
        iv_rank_pct: effIvRank === "" ? null : Number(effIvRank),
      });
      setEvaluation(ev);

      // Сетка цен 50–250% спота: даёт данные для зума (по центру спот, отдаление до 250%).
      const gLo = effSpot * 0.5, gHi = effSpot * 2.5, gN = 220;
      const prices = Array.from({ length: gN }, (_, i) => +(gLo + ((gHi - gLo) * i) / (gN - 1)).toFixed(4));
      const po = await api.computePayoff(portfolioId, { underlying: effUnd, market, dates, prices });
      setPayoff(po);
      // Слайдер по умолчанию — на дате выхода (ближайшая точка сетки), не на экспирации.
      setDateIndex(nearestDateIndex(po.dates, effExit));
    } catch (e: any) {
      setError(e.message);
    } finally { setComputing(false); }
  }

  async function onDelete() {
    if (!id) return;
    if (!confirm("Удалить портфель?")) return;
    await api.deletePortfolio(id);
    nav("/portfolios");
  }

  if (!loaded) return <div style={{ color: "var(--txt-3)", padding: 24 }}>Загрузка…</div>;

  return (
    <>
      <div className="page-tools">
        <input className="title-ed" style={{ flex: 1, minWidth: 180, background: "transparent", border: "none", outline: "none", fontSize: 22, fontWeight: 680, letterSpacing: "-.02em", color: "var(--txt)" }}
          value={name} onChange={(e) => setName(e.target.value)} />
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {!isNew && <button className="btn btn-ghost" onClick={onDelete}>Удалить</button>}
          <button className="btn btn-ghost" onClick={save} disabled={saving}>{saving ? "…" : "Сохранить"}</button>
          <button className="btn btn-primary" onClick={() => compute()} disabled={computing} title="Посчитать профиль доходности и проверить по 5 правилам">
            {computing ? "Считаю…" : "Проверить позицию"}
          </button>
        </div>
      </div>

      {error && <div className="card" style={{ padding: 12, color: "var(--bad)", marginBottom: 12 }}>{error}</div>}

      <div className="layout">
        {/* Левая колонка: рынок + ноги */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="card">
            <div className="card-h"><span>Рынок <span style={{ color: "var(--txt-3)", fontWeight: 400, fontSize: 11 }}>базовый актив и условия входа</span></span></div>
            <div className="card-b">
              <div className="two">
                <div style={{ position: "relative" }}>
                  <div className="lbl"><Tip text={"Что: базовый актив, на который выписан опцион.\nКак: выбери из вотчлиста или впиши любой тикер IBKR/Saxo (GLD, SPY, AAPL…).\nПочему: от него считается всё остальное."}>Тикер</Tip></div>
                  <input className="inp" value={underlying}
                    onChange={(e) => { setUnderlying(e.target.value.toUpperCase()); setTickerOpen(true); }}
                    onFocus={() => setTickerOpen(true)}
                    onBlur={() => setTimeout(() => setTickerOpen(false), 150)}
                    placeholder="Из вотчлиста или вручную" />
                  {tickerOpen && wl.length > 0 && (() => {
                    const q = underlying.trim().toUpperCase();
                    const items = wl.filter((s) =>
                      !q || s.symbol.includes(q) || (s.data_symbol || "").includes(q) || (s.name || "").toUpperCase().includes(q));
                    const list = items.length ? items : wl;   // полный ввод (точное совпадение) → показываем всё
                    const prioC: Record<string, string> = { red: "var(--bad)", yellow: "var(--warn)", green: "var(--ok)" };
                    return (
                      <div className="tkr-dd" onMouseDown={(e) => e.preventDefault() /* не отдаём blur до клика */}>
                        <div className="tkr-dd-h">Вотчлист · {list.length}</div>
                        {list.map((s) => {
                          const dataSym = (s.data_symbol || s.symbol).toUpperCase();
                          return (
                            <div key={s.symbol} className="tkr-dd-it"
                              onClick={() => {
                                setUnderlying(dataSym); setTickerOpen(false);
                                buildStrangle(dataSym);   // сразу собираем стрэнгл по выбранному активу
                              }}>
                              {s.priority && prioC[s.priority] && <span className="dot" style={{ background: prioC[s.priority] }} />}
                              <b>{s.symbol}</b>
                              {s.data_symbol && s.data_symbol !== s.symbol && <span className="via">→{s.data_symbol}</span>}
                              <span className="nm">{s.name}</span>
                              <span className="cls">{s.cls}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <div className="lbl"><Tip text={tip(LBL.spot.t!)}>{LBL.spot.primary}</Tip></div>
                  <input className="inp" type="number" step="0.01" value={spot} onChange={(e) => setSpot(+e.target.value)} />
                </div>
              </div>

              <div className="lbl"><Tip text={tip(LBL.iv.t!)}>{LBL.iv.primary} ({(iv * 100).toFixed(0)}%)</Tip></div>
              <input className="inp" type="number" step="0.01" value={iv} onChange={(e) => setIv(+e.target.value)} />

              <div className="lbl"><Tip text={tip(LBL.ivRank.t!)}>IV Rank, % <span style={{ color: "var(--txt-3)" }}>· 0–100, опционально</span></Tip></div>
              <input className="inp" type="number" min={0} max={100} placeholder="не задан → gate пропускается" value={ivRank}
                onChange={(e) => setIvRank(e.target.value === "" ? "" : Number(e.target.value))} />
              <div className="hint">Не путать с «волатильностью входа (IV)» выше. 0 = IV на годовом минимуме (дёшево), 100 = максимум. Gate отвергает &gt; 40. В IBKR — «52-нед. рейтинг ПВ», в Saxo — «IV Rank». Без него gate не блокирует.</div>

              <div className="lbl"><Tip text={tip(LBL.exitDate.t!)}>{LBL.exitDate.primary}</Tip></div>
              <input className="inp" type="date" value={exitDate} onChange={(e) => setExitDate(e.target.value)} />
              <div className="hint">≈ {horizonLabel(exitDate)}. Вердикт gates считается на эту дату (не в экспирацию).</div>

              {/* Пикер реальной экспирации */}
              <div className="lbl" style={{ justifyContent: "space-between" }}>
                <Tip text={tip(LBL.expiry.t!)}>{LBL.expiry.primary} <span style={{ color: "var(--txt-3)" }}>· реальные даты</span></Tip>
                <button style={{ fontSize: 11, color: "var(--accent-2)", fontWeight: 600 }} onClick={loadExpirations} disabled={loadingExp}>{loadingExp ? "…" : "↓ загрузить"}</button>
              </div>
              {expirations && expirations.expirations_available.length > 0 ? (
                <select className="inp" value={chosenExpiry || legs.find((l) => l.type === "option")?.expiry || expirations.best_expiration || ""} onChange={(e) => chooseExpiry(e.target.value)}>
                  {expirations.expirations_available.map((d) => {
                    const days = Math.round((new Date(d).getTime() - Date.now()) / 86_400_000);
                    const best = d === expirations.best_expiration;
                    const tag = best ? "✓ ОПТИМАЛЬНО" : days < 330 ? "слишком близко" : days > 400 ? "длинновато" : "допустимо";
                    return <option key={d} value={d}>{d} ({days}д) — {tag}</option>;
                  })}
                </select>
              ) : <div className="hint">Нажми «↓ загрузить» — подтянем реальные даты и выберем оптимальную (≈365д).</div>}
              {expirations && expirations.expirations_available.length > 0 && (
                <div className="hint">Выбери срок и нажми «⚡ Собрать стрэнгл» — данные пересчитаются под эту экспирацию, не только под оптимальную.</div>
              )}
              {expirations?.best_expiration_status === "WARN_OUT_OF_WINDOW" && <div className="hint" style={{ color: "var(--warn)" }}>⚠ Нет экспирации в окне 330–400д — выбрана ближайшая.</div>}

              {/* Autofill */}
              <div style={{ borderTop: "1px solid var(--line)", marginTop: 14, paddingTop: 12 }}>
                <div className="lbl" style={{ marginTop: 0, gap: 8 }}>
                  <Tip text={tip(LBL.otm.t!)}>{LBL.otm.primary}</Tip>
                  <input className="inp" style={{ width: 64, padding: "5px 8px" }} type="number" step="0.5" value={pctOtm} onChange={(e) => setPctOtm(+e.target.value)} />
                </div>
                <button className="btn btn-ghost" style={{ width: "100%", justifyContent: "center", marginTop: 8 }} onClick={pullMarket} disabled={autofilling}>{autofilling ? "…" : "↓ Подтянуть spot + IV Rank"}</button>
                <button className="autostr" onClick={autoStrangle} disabled={autofilling}>{autofilling ? "…" : `⚡ Собрать стрэнгл за меня (${pctOtm}% OTM)`}</button>
                {autofilling && pullStep && <div className="pulling"><span className="spin" />Подтягиваю: {pullStep}</div>}
                {!autofilling && marketInfo && <div className="infobox">{marketInfo}</div>}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-h">
              <span>Состав позиции <span style={{ color: "var(--txt-3)", fontWeight: 400, fontSize: 11 }}>опционы + подушка · {legs.length}</span></span>
              <select className="inp" style={{ width: "auto", padding: "6px 9px", fontSize: 12 }} value=""
                onChange={(e) => { if (e.target.value) { setLegs([...legs, blankLeg(e.target.value as any, underlying)]); e.target.value = ""; } }}>
                <option value="">+ Добавить ногу</option>
                <option value="option">Опцион (Call/Put)</option>
                <option value="future">Фьючерс</option>
                <option value="deposit">Депозит</option>
                <option value="bond">Облигация</option>
                <option value="stock">Акция</option>
                <option value="cash">Кэш</option>
              </select>
            </div>
            <div className="card-b">
              {legs.length === 0 && (
                <div style={{ border: "1px dashed var(--line-2)", borderRadius: 10, padding: 14, textAlign: "center", marginBottom: 10 }}>
                  <div style={{ fontSize: 13, color: "var(--txt-2)", marginBottom: 8 }}>Пусто. Создать стандартный long-strangle одним кликом?</div>
                  <button className="btn btn-primary" onClick={addStrangleTemplate}>+ Long Strangle (Call + Put)</button>
                  <div className="hint">потом подставишь реальные strike/premium из Saxo/IBKR; для gates 90/10 и no-loss добавь ногу «Депозит».</div>
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {legs.map((leg, i) => (
                  <LegEditor key={leg._internal_id || i} leg={leg}
                    onChange={(updated) => setLegs(legs.map((l, j) => (j === i ? updated : l)))}
                    onRemove={() => setLegs(legs.filter((_, j) => j !== i))}
                    marketSpot={spot} marketIv={iv} marketRisk={riskFree} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Правая колонка: график + gates */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="card">
            <div className="card-b">
              {payoff ? (
                <PayoffChart payoff={payoff} spot={spot} dateIndex={dateIndex} onDateIndexChange={setDateIndex} />
              ) : (
                <div className="empty" style={{ minHeight: 320 }}>
                  <span className="ic">📈</span>
                  <div style={{ fontWeight: 600, color: "var(--txt-2)" }}>Профиль доходности появится здесь</div>
                  <div style={{ maxWidth: 320, fontSize: 12.5 }}>Собери позицию слева и нажми «Проверить позицию» — покажем суммарный P&amp;L портфеля и отдельные ноги.</div>
                </div>
              )}
            </div>
          </div>

          {/* Цена базового актива: «как актив вообще ходил» — хаи/лои, пресеты Всё/5л/1г/1м.
              BE-коридор из расчёта окупаемости рисуется прямо на графике. */}
          {underlying && (() => {
            const bm0 = payoff ? breakevenFromPayoff(payoff, spot) : null;
            return <PriceHistoryChart symbol={underlying} beLow={bm0?.lowBE} beHigh={bm0?.highBE} />;
          })()}

          {/* Окупаемость премии: на сколько % цена должна сойти вниз/вверх (общий параметр breakeven) */}
          {payoff && (() => {
            const bm = breakevenFromPayoff(payoff, spot);
            if (bm.downPct == null && bm.upPct == null) return null;
            return (
              <div className="card"><div className="card-b becard">
                <div className="be-h"><Tip text={tip(LBL.breakeven.t!)}>Чтобы окупить премию, цене нужно сойти:</Tip></div>
                <div className="be-row">
                  <div className="be-leg down">
                    <span className="be-arrow">↓</span>
                    <span className="be-pct">{bm.downPct != null ? `−${bm.downPct.toFixed(1)}%` : "—"}</span>
                    <span className="be-be">{bm.lowBE != null ? `до ${bm.lowBE.toFixed(2)}` : ""}</span>
                  </div>
                  <div className="be-leg up">
                    <span className="be-arrow">↑</span>
                    <span className="be-pct">{bm.upPct != null ? `+${bm.upPct.toFixed(1)}%` : "—"}</span>
                    <span className="be-be">{bm.highBE != null ? `до ${bm.highBE.toFixed(2)}` : ""}</span>
                  </div>
                </div>
                <div className="hint" style={{ marginTop: 8 }}>Внутри этого коридора (от спота {spot.toFixed(2)}) опционы в убытке — его и должна покрыть подушка. Депозит на эти точки нуля не влияет.</div>
              </div></div>
            );
          })()}

          {evaluation && <GatesPanel evaluation={evaluation} />}
        </div>
      </div>
    </>
  );
}

// Суффикс качества данных (P1-3): помечаем устаревшие/кешированные котировки.
function qualitySuffix(stale?: boolean, ageS?: number): string {
  if (stale) return " ⚠ STALE (источник недоступен, последнее значение)";
  if (ageS && ageS > 1) return ` · из кеша (${Math.round(ageS)}с)`;
  return "";
}

// Дата выхода по умолчанию: сегодня + 9 месяцев (методика «Айкидо»).
function defaultExitDate(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 9);
  return d.toISOString().slice(0, 10);
}

// Человекочитаемый горизонт от сегодня до даты выхода.
function horizonLabel(exitISO: string): string {
  const days = Math.round((new Date(exitISO).getTime() - Date.now()) / 86_400_000);
  if (days <= 0) return "сегодня";
  const months = Math.round(days / 30.4);
  return months >= 1 ? `${days} дн. (~${months} мес.)` : `${days} дн.`;
}

// Индекс ближайшей к target даты в сетке (для дефолтной позиции слайдера на дате выхода).
function nearestDateIndex(dates: string[], targetISO: string): number {
  const t = new Date(targetISO).getTime();
  let best = 0, bestDelta = Infinity;
  dates.forEach((d, i) => {
    const delta = Math.abs(new Date(d).getTime() - t);
    if (delta < bestDelta) { bestDelta = delta; best = i; }
  });
  return best;
}

// Build a date grid from today to last expiry with N points
function makeDateGrid(startISO: string, endISO: string, points: number): string[] {
  const start = new Date(startISO).getTime();
  const end = new Date(endISO).getTime();
  if (end <= start) return [endISO];
  const grid: string[] = [];
  for (let i = 0; i < points; i++) {
    const t = start + ((end - start) * i) / (points - 1);
    grid.push(new Date(t).toISOString().slice(0, 10));
  }
  return grid;
}
